import React, { useEffect, useState } from 'react';
import { X, Loader2, CheckCircle2, XCircle, Send, Bell, Download, Plus } from 'lucide-react';
import { CLIENT_TYPE_LABELS, formatCurrency, formatDate, STAGE_STATUS_LABELS } from '../client-portal/clientPortalApi';
import AdminMessagesPanel from './AdminMessagesPanel';
import AdminProposalsPanel from './AdminProposalsPanel';
import AdminStrategyPanel from './AdminStrategyPanel';
import AdminPostSettlementPanel from './AdminPostSettlementPanel';
import AdminReviewsPanel from './AdminReviewsPanel';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const STAGE_STATUS_OPTIONS = [
  'not_started',
  'in_progress',
  'waiting_on_client',
  'waiting_on_lender',
  'completed',
  'issue',
];

export default function AdminClientDetailDrawer({ clientId, token, onClose, onChanged }) {
  const auth = { Authorization: `Bearer ${token}` };
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('journey');
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    const res = await fetch(`${backendUrl}/api/admin/clients/${clientId}`, { headers: auth });
    if (res.ok) setData(await res.json());
  };

  useEffect(() => {
    (async () => { try { await reload(); } finally { setLoading(false); } })();
    // eslint-disable-next-line
  }, [clientId]);

  const updateStage = async (stageKey, status) => {
    setBusy(true);
    try {
      await fetch(`${backendUrl}/api/admin/clients/${clientId}/journey`, {
        method: 'PATCH',
        headers: { ...auth, 'Content-Type': 'application/json' },
        body: JSON.stringify({ stage_key: stageKey, status }),
      });
      await reload();
      onChanged?.();
    } finally {
      setBusy(false);
    }
  };

  const reviewDoc = async (docId, status, reason = null) => {
    setBusy(true);
    try {
      await fetch(`${backendUrl}/api/admin/documents/${docId}`, {
        method: 'PATCH',
        headers: { ...auth, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, reason }),
      });
      await reload();
    } finally {
      setBusy(false);
    }
  };

  const requestDoc = async () => {
    const label = window.prompt('Document label?');
    if (!label) return;
    const category = window.prompt('Category (e.g. payslips, identity, bank_statements)?', 'other') || 'other';
    setBusy(true);
    try {
      await fetch(`${backendUrl}/api/admin/clients/${clientId}/documents`, {
        method: 'POST',
        headers: { ...auth, 'Content-Type': 'application/json' },
        body: JSON.stringify({ category, label }),
      });
      await reload();
    } finally {
      setBusy(false);
    }
  };

  const pushNotification = async () => {
    const title = window.prompt('Notification title?');
    if (!title) return;
    const body = window.prompt('Message?', '') || '';
    setBusy(true);
    try {
      await fetch(`${backendUrl}/api/admin/clients/${clientId}/notifications`, {
        method: 'POST',
        headers: { ...auth, 'Content-Type': 'application/json' },
        body: JSON.stringify({ title, body, type: 'info' }),
      });
      await reload();
    } finally {
      setBusy(false);
    }
  };

  const downloadDoc = async (doc) => {
    const res = await fetch(`${backendUrl}/api/admin/documents/${doc.id}/download`, { headers: auth });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = doc.original_filename || 'document';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-end sm:items-stretch justify-end" onClick={onClose}>
      <div
        className="bg-white w-full sm:w-[640px] h-[92vh] sm:h-full overflow-y-auto rounded-t-2xl sm:rounded-none ring-1 ring-slate-200"
        onClick={(e) => e.stopPropagation()}
        data-testid="admin-client-detail"
      >
        <header className="sticky top-0 bg-white border-b border-slate-100 px-5 py-3 flex items-center justify-between z-10">
          <div className="min-w-0">
            <div className="font-semibold text-slate-900 truncate">{data?.client?.name || 'Client'}</div>
            <div className="text-xs text-slate-500 truncate">{data?.client?.email}</div>
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="close-detail-drawer" className="text-slate-500 hover:text-slate-900">
            <X className="w-5 h-5" />
          </button>
        </header>

        {loading || !data ? (
          <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : (
          <>
            <div className="px-5 py-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm border-b border-slate-100">
              <KV label="Type" value={CLIENT_TYPE_LABELS[data.client.client_type] || data.client.client_type} />
              <KV label="Broker" value={data.client.broker_name} />
              <KV label="Loan amount" value={data.client.loan_amount ? formatCurrency(data.client.loan_amount) : '—'} />
              <KV label="Created" value={formatDate(data.client.created_at)} />
            </div>

            <nav className="flex border-b border-slate-100 px-2 overflow-x-auto">
              {['journey', 'documents', 'messages', 'proposals', 'strategy', 'reviews', 'post_settlement', 'notifications'].map((t) => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  data-testid={`tab-${t}`}
                  className={`px-4 py-2.5 text-sm font-medium capitalize whitespace-nowrap ${tab === t ? 'text-emerald-600 border-b-2 border-emerald-500' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  {t.replace(/_/g, ' ')}
                </button>
              ))}
            </nav>

            {tab === 'journey' && (
              <ul className="p-5 space-y-2">
                {data.journey.map((s) => (
                  <li key={s.stage_key} className="flex items-center gap-2 flex-wrap" data-testid={`admin-stage-${s.stage_key}`}>
                    <div className="flex-1 min-w-[160px]">
                      <div className="text-sm font-medium text-slate-800">{s.stage_label}</div>
                      {s.completed_at && <div className="text-[11px] text-slate-500">Completed {formatDate(s.completed_at)}</div>}
                    </div>
                    <select
                      disabled={busy}
                      value={s.status}
                      onChange={(e) => updateStage(s.stage_key, e.target.value)}
                      data-testid={`stage-select-${s.stage_key}`}
                      className="px-2 py-1 rounded-md ring-1 ring-slate-200 text-xs bg-white"
                    >
                      {STAGE_STATUS_OPTIONS.map((st) => (
                        <option key={st} value={st}>{STAGE_STATUS_LABELS[st]}</option>
                      ))}
                    </select>
                  </li>
                ))}
              </ul>
            )}

            {tab === 'documents' && (
              <div className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs text-slate-500 uppercase tracking-wide font-semibold">{data.documents.length} document(s)</span>
                  <button onClick={requestDoc} className="inline-flex items-center gap-1 text-sm text-emerald-700 hover:underline" data-testid="request-doc">
                    <Plus className="w-3.5 h-3.5" /> Request document
                  </button>
                </div>
                <ul className="space-y-2">
                  {data.documents.map((d) => (
                    <li key={d.id} className="rounded-lg ring-1 ring-slate-200 p-3 text-sm" data-testid={`admin-doc-${d.id}`}>
                      <div className="flex items-start gap-2 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-slate-800">{d.label}</div>
                          <div className="text-xs text-slate-500">
                            <span className="capitalize">{d.category}</span>
                            {' · '}<span className="capitalize">{d.status.replace(/_/g, ' ')}</span>
                            {d.original_filename && <> · {d.original_filename}</>}
                          </div>
                          {d.rejection_reason && <div className="mt-1 text-xs text-rose-700">{d.rejection_reason}</div>}
                        </div>
                        <div className="flex items-center gap-1">
                          {d.original_filename && (
                            <button onClick={() => downloadDoc(d)} className="p-1.5 rounded hover:bg-slate-100 text-slate-600" title="Download" data-testid={`dl-doc-${d.id}`}>
                              <Download className="w-4 h-4" />
                            </button>
                          )}
                          {d.status === 'uploaded' && (
                            <>
                              <button onClick={() => reviewDoc(d.id, 'approved')} className="p-1.5 rounded bg-emerald-50 text-emerald-700 hover:bg-emerald-100" title="Approve" data-testid={`approve-doc-${d.id}`}>
                                <CheckCircle2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => {
                                  const reason = window.prompt('Reason for rejection?') || 'Please re-upload';
                                  reviewDoc(d.id, 'needs_replacement', reason);
                                }}
                                className="p-1.5 rounded bg-rose-50 text-rose-700 hover:bg-rose-100"
                                title="Re-request"
                                data-testid={`reject-doc-${d.id}`}
                              >
                                <XCircle className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {tab === 'messages' && <AdminMessagesPanel clientId={clientId} token={token} />}
            {tab === 'proposals' && <AdminProposalsPanel clientId={clientId} token={token} />}
            {tab === 'strategy' && <AdminStrategyPanel clientId={clientId} token={token} />}
            {tab === 'reviews' && <AdminReviewsPanel clientId={clientId} token={token} />}
            {tab === 'post_settlement' && <AdminPostSettlementPanel clientId={clientId} token={token} />}

            {tab === 'notifications' && (
              <div className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs text-slate-500 uppercase tracking-wide font-semibold">{data.notifications.length} update(s)</span>
                  <button onClick={pushNotification} className="inline-flex items-center gap-1 text-sm text-emerald-700 hover:underline" data-testid="push-notif">
                    <Send className="w-3.5 h-3.5" /> Send update
                  </button>
                </div>
                <ul className="space-y-2">
                  {data.notifications.map((n) => (
                    <li key={n.id} className="rounded-lg ring-1 ring-slate-200 p-3 text-sm">
                      <div className="flex items-start gap-2">
                        <Bell className="w-4 h-4 text-slate-400 mt-0.5" />
                        <div className="flex-1">
                          <div className="font-medium text-slate-800">{n.title}</div>
                          <div className="text-slate-600">{n.body}</div>
                          <div className="text-[11px] text-slate-400 mt-0.5">{formatDate(n.created_at)}{n.read ? ' · read' : ''}</div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-slate-800 truncate">{value || '—'}</div>
    </div>
  );
}
